/**
 * AI CONTEXT
 * Purpose: registry for command/global search options and callbacks.
 * Exposes: register/unregister options, callback registry, option label renderer.
 * Used by: CommandPalette-related flows.
 * Importance: cross-feature discoverability/navigation plumbing.
 * Refactor: avoid state closures in register/unregister by using functional updates.
 */
import { noop } from "lodash"
import { createContext, type ReactNode, useCallback, useState } from "react"

export interface SearchOption {
  value: string
  label: ReactNode
}

export interface SearchOptionCategory {
  label: ReactNode
  options: SearchOption[]
}

interface GlobalSearchContextType {
  registerSearchOptions: (options: SearchOptionCategory[]) => void
  unregisterSearchOptions: (options: SearchOptionCategory[]) => void
  searchOptions: SearchOptionCategory[]
  renderOption: (
    title: string,
    count: number
  ) => { title: string; label: ReactNode }
  callbacks: unknown[]
  registerCallback: (cb: unknown) => void
}

export const GlobalSearchContext = createContext<GlobalSearchContextType>({
  registerSearchOptions: noop,
  unregisterSearchOptions: noop,
  searchOptions: [],
  renderOption: () => ({ title: "", label: <></> }),
  callbacks: [],
  registerCallback: noop,
})

// const renderLabel = (title: string): ReactNode => (
//   <span>
//     {title}
//     <a
//       style={{ float: "right" }}
//       href="https://www.google.com/search?q=antd"
//       target="_blank"
//       rel="noopener noreferrer"
//     >
//       more
//     </a>
//   </span>
// )

const renderOption = (
  title: string,
  count: number
): { title: string; label: ReactNode } => ({
  title,
  label: (
    <div
      onClick={console.log}
      style={{
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      {title}
      <span>{count}</span>
    </div>
  ) as ReactNode,
})

// const options: SearchOptionCategory[] = [
//   {
//     label: renderLabel("Libraries"),
//     options: [
//       renderOption("AntDesign", 10000),
//       renderOption("AntDesign UI", 10600),
//     ],
//   },
//   {
//     label: renderLabel("Solutions"),
//     options: [
//       renderOption("AntDesign UI FAQ", 60100),
//       renderOption("AntDesign FAQ", 30010),
//     ],
//   },
//   {
//     label: renderLabel("Articles"),
//     options: [renderOption("AntDesign design language", 100000)],
//   },
// ]

const GlobalSearchContextProvider = ({ children }: { children: ReactNode }) => {
  const [callbacks, setCallbacks] = useState<unknown[]>([])
  const [searchOptions, setSearchOptions] = useState<SearchOptionCategory[]>([])

  const registerCallback = (callback: unknown) => {
    setCallbacks((callbacks) => [...callbacks, callback])
  }

  const registerSearchOptions = useCallback(
    (newSearchOptions: SearchOptionCategory[]) => {
      setSearchOptions([...searchOptions, ...newSearchOptions])
    },
    [searchOptions]
  )

  const unregisterSearchOptions = useCallback(
    (newSearchOptions: SearchOptionCategory[]) => {
      const resultSearchOptions = searchOptions.filter(
        (searchOption: SearchOptionCategory) =>
          !newSearchOptions.some(
            (newSearchOption) => searchOption === newSearchOption
          )
      )
      console.log("resultSearchOptions => ", resultSearchOptions)
      setSearchOptions(resultSearchOptions)
    },
    [searchOptions]
  )

  const defaultValue = {
    registerSearchOptions,
    unregisterSearchOptions,
    searchOptions,
    renderOption,
    callbacks,
    registerCallback,
  }

  return (
    <GlobalSearchContext.Provider value={defaultValue}>
      {children}
    </GlobalSearchContext.Provider>
  )
}

export default GlobalSearchContextProvider
